import { useRef } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Grade, GradeChangeInput } from '@/pages/AccountInformation/AccountInformation.types';
import { useAriaInvalid } from '@/hooks';
import styles from './SelectInput.styles';
import { BodySmall } from '@/design-system';
import colors from '@/theme/colors';

interface SelectInputProps {
  label: string;
  childUid: string;
  options: Grade[];
  value: Grade;
  onChange: (input: GradeChangeInput, childUId: string) => void;
  error?: boolean;
}

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  childUid,
  options,
  value,
  onChange,
  error = false,
}) => {
  const handleChange = (event: SelectChangeEvent) => {
    const grade = options.find(option => option.id === event.target.value);
    if (grade) {
      onChange(grade, childUid);
    }
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const labelId = `gradeLabelId${childUid}`;
  const listboxId = `gradeListbox${childUid}`;

  useAriaInvalid(
    () => wrapperRef.current?.querySelector<HTMLDivElement>('.MuiSelect-select'),
    error,
    [childUid],
  );

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <BodySmall
        id={labelId}
        className={styles.label}
        color={colors.charcoal}
      >
        {label}
      </BodySmall>
      <Select
        labelId={labelId}
        id={`gradeInput${childUid}`}
        variant="standard"
        fullWidth
        className={styles.container}
        value={value?.id.toString() || ''}
        onChange={handleChange}
        inputProps={{
          name: `grade-${childUid}`,
          'data-testid': 'gradeInput',
        }}
        SelectDisplayProps={{
          'aria-controls': listboxId,
        }}
        MenuProps={{
          MenuListProps: {
            id: listboxId,
            'aria-labelledby': labelId,
          },
        }}
      >
        {options.map(({ id, description }) => (
          <MenuItem key={id} value={id} data-testid="gradeOption" className={styles.optionItem}>
            {description}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
};
